/**
 * Shared tile chrome. Same shape for all 3 pillars so the Catalog reads as
 * one cohesive surface. Coming-soon variant lowers opacity + shows badge +
 * disables the install affordance.
 *
 * Per Ean 2026-06-10 catalog density pass — action vs descriptor split:
 *   - DESCRIPTOR zone (top-right of header): Coming Soon pill OR Installed
 *     badge. Both communicate tile status at a glance. Same visual home.
 *   - ACTION zone (bottom row): Install / Request access / Upvote — only
 *     rendered when there's a real action to take. On Installed+Active
 *     tiles the row collapses entirely (whole-tile click IS the action).
 *   - The whole tile becomes the click target when onOpenDetail is defined.
 *     Hover lift + teal border + chevron signals "this opens" without
 *     eating card real estate with a Details link.
 */

import { Button } from '@olytic/foundation';
import type React from 'react';

import { InstalledPill, ComingSoonPill } from '../../components/StatusPill.tsx';
import { UpvoteChip } from '../../components/UpvoteChip.tsx';

export function CatalogTile({
  title, subtitle, logo, categoryLabel, tags, comingSoon, canInstall, installing,
  onInstall, onRequestAccess, onSuggestPriority: _onSuggestPriority, upvoteCount, isInstalled, onUpvote, hasUpvoted, onOpenDetail,
}: {
  title: string;
  subtitle: string | null;
  logo?: React.ReactNode;
  categoryLabel?: string | null;
  /** Push #75 — small uppercase pills rendered below the description. */
  tags?: string[];
  comingSoon: boolean;
  canInstall: boolean;
  installing: boolean;
  onInstall: () => void;
  onRequestAccess: () => void;
  onSuggestPriority: () => void;
  /** Global upvote count (migration 0051). */
  upvoteCount: number;
  /** Per Ean 2026-06-10: when true, render "Installed in workspace" indicator
   * instead of Install/Ask-admin button. Lets users browse the full catalog
   * (incl. things already installed) without confusing them about whether
   * to install a duplicate. */
  isInstalled: boolean;
  /** Coming Soon one-click upvote handler + already-upvoted state. */
  onUpvote: () => void;
  hasUpvoted: boolean;
  onOpenDetail?: () => void;
}) {
  const isClickable = onOpenDetail !== undefined && !comingSoon;
  // The bottom row only renders when there's an action to surface. On an
  // Installed+Active tile, the only thing to do is open it — the whole-tile
  // click handles that.
  const hasBottomAction = comingSoon || !isInstalled;

  const onCardClick = isClickable
    ? () => onOpenDetail()
    : undefined;
  const onCardKeyDown = isClickable
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Only keys pressed on the tile itself open it. Enter/Space on an
        // inner button bubbles here too, and must activate that button
        // instead (PLAT-288, card ccf5f433).
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetail();
        }
      }
    : undefined;
  // Inner buttons must stopPropagation so clicking Install/Upvote/etc.
  // doesn't also fire the card-level onOpenDetail navigation.
  const stop = (e: React.MouseEvent) => { e.stopPropagation(); };

  return (
    <div
      className={`card card-padded${isClickable ? ' tile-clickable' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        opacity: comingSoon ? 0.78 : 1,
        background: comingSoon ? 'var(--olytic-surface)' : 'var(--olytic-white)',
      }}
      {...(onCardClick !== undefined ? { onClick: onCardClick } : {})}
      {...(onCardKeyDown !== undefined ? { onKeyDown: onCardKeyDown } : {})}
      {...(isClickable ? { role: 'button', tabIndex: 0, 'aria-label': `Open ${title} details` } : {})}
    >
      {isClickable && <span className="tile-chevron" aria-hidden="true">→</span>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {logo}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--olytic-obsidian)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </h3>
            {/* Descriptor zone: Coming Soon OR Installed, mutually exclusive.
                Both are tile-status, not actions. Reserve slight right pad
                so the hover chevron doesn't collide on Installed tiles. */}
            {/* Push #22 rule 2: status indicators on cards are always pills.
                Was bare "Installed" floating-text + a separate neutral pill
                for coming-soon. now both render through StatusPill helpers
                so the pill chrome (radius, padding, font) stays identical
                across surfaces. */}
            {comingSoon ? (
              <ComingSoonPill />
            ) : isInstalled ? (
              <span
                title="Already installed in your workspace"
                style={{ paddingRight: isClickable ? 18 : 0 }}
              >
                <InstalledPill />
              </span>
            ) : null}
          </div>
          {categoryLabel !== null && categoryLabel !== undefined && categoryLabel !== '' && (
            <p
              className="muted"
              style={{ margin: '2px 0 0', fontSize: '11px' }}
            >
              {categoryLabel}
            </p>
          )}
        </div>
      </div>
      {subtitle !== null && subtitle !== '' && (
        <p
          style={{
            margin: 0,
            fontSize: '12.5px',
            color: 'var(--fg-muted)',
            lineHeight: 1.45,
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {subtitle}
        </p>
      )}
      {/* Push #75. tag pills (Ean dogfood 2026-06-15). Per-pillar tag
         labels surface what this template is good at without needing the
         user to drill into the detail page. Models pass `recommended_for`
         (REASONING / CODE / FAST etc.); future capability types can pass
         their own tag arrays. Rendered below the description, above the
         action row, so the visual hierarchy reads name → sentence → tags
         → action. */}
      {tags !== undefined && tags.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          {tags.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 6,
                background: 'color-mix(in srgb, var(--olytic-teal) 10%, transparent)',
                color: 'var(--olytic-teal)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      {hasBottomAction && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: '4px',
          }}
        >
          {comingSoon ? (
            /* Coming Soon — Push #28 UpvoteChip. Single chip combines the
               heart icon + count, animates on increment, swaps to filled
               teal when the user has upvoted. Click is idempotent server-
               side via the upvoted_slugs array in sys__account_user
               .ui_preferences (Migration H, 2026-06-24). */
            <div onClick={stop} style={{ display: 'inline-flex' }}>
              <UpvoteChip
                count={upvoteCount + (hasUpvoted ? 1 : 0)}
                upvoted={hasUpvoted}
                onClick={hasUpvoted ? () => undefined : onUpvote}
              />
            </div>
          ) : canInstall ? (
            <Button
              type="button"
              variant="brand"
              size="sm"
              onClick={(e) => { stop(e); onInstall(); }}
              disabled={installing}
            >
              {installing ? 'Installing…' : 'Install to workspace'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="brand"
              size="sm"
              onClick={(e) => { stop(e); onRequestAccess(); }}
            >
              Request access
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
